import { motion } from "framer-motion";

/**
 * Shared editorial primitives used across pages.
 * Keep this small — one place to tune the app-wide reveal cadence.
 */

export function MaskLine({ children, delay = 0, className = "" }) {
  return (
    <span className={`reveal-mask ${className}`}>
      <motion.span
        className="reveal-line"
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.05, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export function Reveal({ children, delay = 0, y = 24, className = "", once = true }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, margin: "-80px" }}
      transition={{ duration: 0.85, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function Eyebrow({ children, className = "" }) {
  return (
    <p className={`text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-inkMuted ${className}`}>
      {children}
    </p>
  );
}

/**
 * Big editorial page header that lives at the top of every non-home page.
 * Renders: rule + eyebrow, huge sans display line, sans/italic accented line,
 * and an optional intro paragraph.
 */
export function EditorialHeader({
  chapter,       // e.g. "Chapter 02"
  eyebrow,       // e.g. "The Shop"
  title,         // e.g. "Systems built for real school life"
  italicWord,    // optional word rendered in Fraunces italic sage color
  intro,         // optional paragraph
  right,         // optional right-side content (image, form)
  testid,
}) {
  return (
    <section data-testid={testid} className="relative overflow-hidden pt-14 md:pt-20 pb-16 md:pb-20">
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute inset-0 bg-brand-bg" />
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-brand-sage/12 blur-[110px]" />
        <div className="absolute top-1/2 -left-32 w-[380px] h-[380px] rounded-full bg-brand-accent/10 blur-[110px]" />
        <div className="absolute inset-0 grain opacity-40" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-inkMuted">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="flex items-center gap-2"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-brand-accent" />
            {chapter}, {eyebrow}
          </motion.span>
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hidden md:inline"
          >
            One Step Ahead, editorial
          </motion.span>
        </div>

        <div className="mt-8 md:mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className={right ? "lg:col-span-8" : "lg:col-span-12"}>
            <h1 className="font-display font-medium tracking-[-0.03em] text-brand-ink leading-[0.98] text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
              <span className="block"><MaskLine delay={0.05}>{title}</MaskLine></span>
              {italicWord ? (
                <span className="block mt-1">
                  <MaskLine delay={0.18}>
                    <span className="font-editorial italic font-normal text-brand-sage">{italicWord}</span>
                  </MaskLine>
                </span>
              ) : null}
            </h1>

            {intro ? (
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.85, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="mt-8 max-w-xl text-base sm:text-lg text-brand-inkSoft leading-relaxed"
              >
                {intro}
              </motion.p>
            ) : null}
          </div>
          {right ? <div className="lg:col-span-4">{right}</div> : null}
        </div>
      </div>
    </section>
  );
}

/**
 * Slow editorial marquee band, single line with alternating star bullets.
 * items: array of strings to loop.
 */
export function MarqueeBand({ items, testid = "editorial-marquee" }) {
  return (
    <section
      aria-hidden="true"
      data-testid={testid}
      className="relative border-y border-brand-border bg-brand-bg overflow-hidden py-6 md:py-8"
    >
      <div className="marquee-track flex items-center gap-14 whitespace-nowrap will-change-transform">
        {[...items, ...items, ...items].map((t, i) => (
          <span key={i} className="flex items-center gap-14">
            <span className="font-editorial italic text-brand-ink text-4xl md:text-6xl lg:text-7xl leading-none">
              {t}
            </span>
            <span className="text-brand-accent text-3xl md:text-5xl leading-none">✳</span>
          </span>
        ))}
      </div>
    </section>
  );
}
